const LS_KEY = 'tn_auth';
const LS_VERIFIER = 'tn_pkce_verifier';
const LS_PROCESSED_CODE = 'tn_processed_code';

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (let i = 0; i < bytes.byteLength; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256(str: string): Promise<string> {
  const data = new TextEncoder().encode(str);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
}

import { redirect } from 'react-router-dom';
import { ConfigManager } from '../config/ConfigManager';

async function getEnv() {
  const cfg = await ConfigManager.getInstance().getConfig();
  return {
    domain: (cfg as any).COGNITO_DOMAIN || '',
    clientId: (cfg as any).COGNITO_CLIENT_ID || '',
    redirectUri: (cfg as any).CALLBACK_URL || '',
    region: (cfg as any).COGNITO_REGION || ''
  };
}

type StoredTokens = {
  id_token: string;
  access_token?: string;
  refresh_token?: string;
  exp?: number;
};

function getStore(): StoredTokens | null {
  const raw = localStorage.getItem(LS_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function setStore(v: StoredTokens | null) {
  if (!v) {
    localStorage.removeItem(LS_KEY);
    return;
  }
  localStorage.setItem(LS_KEY, JSON.stringify(v));
}

function parseJwtPayload(idToken: string): any {
  try {
    const parts = idToken.split('.');
    if (parts.length < 2) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    console.log('JWT Payload parsed:', {
      sub: payload.sub,
      email: payload.email,
      exp: payload.exp,
      iat: payload.iat,
      iss: payload.iss,
      aud: payload.aud,
      token_use: payload.token_use,
      auth_time: payload.auth_time
    });
    return payload;
  } catch (error) {
    console.error('Failed to parse JWT payload:', error);
    return null;
  }
}

function parseJwtExp(idToken: string): number | undefined {
  try {
    const payload = parseJwtPayload(idToken);
    return payload && typeof payload.exp === 'number' ? payload.exp : undefined;
  } catch {
    return undefined;
  }
}

async function buildAuthorizeUrl(): Promise<string> {
  const { domain, clientId, redirectUri } = await getEnv();
  const verifier = base64UrlEncode(crypto.getRandomValues(new Uint8Array(32)).buffer);
  localStorage.setItem(LS_VERIFIER, verifier);
  const challenge = await sha256(verifier);
  const url = new URL(`${domain}/oauth2/authorize`);
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('scope', 'openid email profile');
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('code_challenge', challenge);
  const lastPath = window.location.pathname + window.location.search;
  url.searchParams.set('state', encodeURIComponent(lastPath || '/'));
  return url.toString();
}

async function exchangeCode(code: string): Promise<StoredTokens> {
  const { domain, clientId, redirectUri } = await getEnv();
  const verifier = localStorage.getItem(LS_VERIFIER) || '';
  
  console.log('Token exchange attempt:', {
    domain,
    clientId,
    redirectUri,
    code: code.substring(0, 10) + '...',
    hasVerifier: !!verifier
  });
  
  const body = new URLSearchParams();
  body.set('grant_type', 'authorization_code');
  body.set('client_id', clientId);
  body.set('code', code);
  body.set('redirect_uri', redirectUri);
  body.set('code_verifier', verifier);
  
  return fetch(`${domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body
  }).then(resp => {
    if (!resp.ok) {
      return resp.text().then(errorText => {
        console.error('Token exchange failed:', resp.status, resp.statusText, errorText);
        throw new Error(`Token exchange failed: ${resp.status} ${resp.statusText} - ${errorText}`);
      });
    }
    
    return resp.json().then(json => {
      console.log('Token exchange successful');
      
      const idToken = json.id_token as string;
      const exp = parseJwtExp(idToken);
      return {
        id_token: idToken,
        access_token: json.access_token as string | undefined,
        refresh_token: json.refresh_token as string | undefined,
        exp
      };
    });
  }).catch(error => {
    console.error('Token exchange error:', error);
    throw error;
  });
}

async function refresh(refreshToken: string): Promise<StoredTokens> {
  const { domain, clientId } = await getEnv();
  const body = new URLSearchParams();
  body.set('grant_type', 'refresh_token');
  body.set('client_id', clientId);
  body.set('refresh_token', refreshToken);

  const resp = await fetch(`${domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body
  });
  if (!resp.ok) throw new Error('Token refresh failed');
  const json = await resp.json();
  const idToken = json.id_token as string;
  const exp = parseJwtExp(idToken);
  return {
    id_token: idToken,
    access_token: json.access_token as string | undefined,
    refresh_token: json.refresh_token as string | undefined,
    exp
  };
}

function isExpiringSoon(exp?: number): boolean {
  if (!exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return exp - now < 60;
}

export const authService = {
  async login(): Promise<void> {
    const url = await buildAuthorizeUrl();
    window.location.assign(url);
  },

  async handleCallback(currentUrl: string): Promise<void> {
    const url = new URL(currentUrl);
    const code = url.searchParams.get('code');
    if (!code) throw new Error('Missing code');
    
    // Prevent processing the same code multiple times
    const processedCode = localStorage.getItem(LS_PROCESSED_CODE);
    if (processedCode === code) {
      console.log('Code already processed, skipping token exchange');
      return;
    }
    
    // Mark this code as being processed
    localStorage.setItem(LS_PROCESSED_CODE, code);
    
    return exchangeCode(code).then(tokens => {
      console.log('Token exchange successful:', tokens);
      setStore(tokens);
      // Clean up the processed code and PKCE verifier after successful exchange
      localStorage.removeItem(LS_PROCESSED_CODE);
      localStorage.removeItem(LS_VERIFIER);      
      return tokens;
    }).catch(error => {
      // Clean up on error too
      localStorage.removeItem(LS_PROCESSED_CODE);
      console.error('Token exchange error:', error);
      throw error;
    }).then(() => {
      // Callback handled successfully
      // Set window href to re-trigger redirect
      // Slow down reload a bit
      // Show notification success
      window.dispatchEvent(new CustomEvent('tn:authenticated', { detail: { at: Date.now() } }));
      setTimeout(() => {
        window.location.href = '/';
      }, 500);
    });
  },

  async getIdToken(): Promise<string | null> {
    const s = getStore();
    if (!s) return null;
    return s.id_token || null;
  },

  async getAuthHeader(): Promise<string | null> {
    console.log('Getting auth header...');
    const s = getStore();
    if (!s) {
      console.log('No stored tokens found');
      return null;
    }
    
    console.log('Stored token info:', {
      hasIdToken: !!s.id_token,
      hasRefreshToken: !!s.refresh_token,
      exp: s.exp,
      isExpiringSoon: isExpiringSoon(s.exp)
    });
    
    if (isExpiringSoon(s.exp)) {
      console.log('Token is expiring soon, attempting refresh...');
      try {
        if (s.refresh_token) {
          const fresh = await refresh(s.refresh_token);
          setStore(fresh);
          console.log('Token refreshed successfully');
          return `Bearer ${fresh.id_token}`;
        } else {
          console.log('No refresh token available');
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        // Don't return null here, try to use the existing token
      }
    }
    
    const authHeader = `Bearer ${s.id_token}`;
    console.log('Returning auth header with token (first 50 chars):', authHeader.substring(0, 50) + '...');
    
    // Log the JWT payload for debugging
    parseJwtPayload(s.id_token);
    
    return authHeader;
  },

  async refreshIfNeeded(force: boolean = false): Promise<boolean> {
    const s = getStore();
    if (!s) return false;
    if (force || isExpiringSoon(s.exp)) {
      if (!s.refresh_token) return false;
      const fresh = await refresh(s.refresh_token);
      setStore(fresh);
      return true;
    }
    return false;
  },

  // Returns true if an authorization code is currently being processed by the callback
  isProcessing(): boolean {
    return !!localStorage.getItem(LS_PROCESSED_CODE);
  },

  isAuthenticated(): boolean {
    const s = getStore();
    if (!s) return false;
    if (!s.id_token) return false;
    if (isExpiringSoon(s.exp)) return false;
    return true;
  },

  logout(): void {
    setStore(null);
    localStorage.removeItem(LS_VERIFIER);
    localStorage.removeItem(LS_PROCESSED_CODE);
  },

  async getLogoutUrl(): Promise<string> {
    try {
      const { domain, clientId } = await getEnv();
      const logoutUrl = new URL(`${domain}/logout`);
      logoutUrl.searchParams.set('client_id', clientId);
      logoutUrl.searchParams.set('logout_uri', window.location.origin);
      return logoutUrl.toString();
    } catch (error) {
      console.error('Failed to build logout URL:', error);
      return '/login';
    }
  },

  async logoutAndRedirect(): Promise<void> {
    this.logout();
    const logoutUrl = await this.getLogoutUrl();
    window.location.assign(logoutUrl);
  }
};
